import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';
import { useAuth } from '../../hooks/useAuth';
import MemberTimeline from '../t2m/MemberTimeline';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function toISO(date) { return date.toISOString().split('T')[0]; }
function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }
function fmtDisplay(iso) {
  if (!iso) return '';
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
function yesNo(val) {
  if (val === true  || val === 'true'  || val === 'Yes') return <Badge color="#22c55e">Yes</Badge>;
  if (val === false || val === 'false' || val === 'No')  return <Badge color="#6b7280">No</Badge>;
  return <span style={{ color: '#555' }}>—</span>;
}

const TRIAL_ASK_RESULTS = [
  'Sold Trial','Sold Membership','Sold Punch Card','Sales Follow-Up Needed',
  'Declined — Price','Declined — Timing','Declined — Not Ready',
  'Declined — Prefers Drop In','Declined — Already Plays Elsewhere',
  'Declined — Not Enough Time to Commit','Failed to Ask','No Show',
];
const SALE_TYPES   = ['Trial Sold','Membership Sold','Sold Punch Card'];
const LEAD_SOURCES = ['Digital Ad','Website','App','Walk-In','Referral','Lead Engine','Other'];
const LEVELS       = ['Beginner','Intermediate','Advanced','Unknown'];
const TIME_OPTIONS = [
  '8:00 AM','8:30 AM','9:00 AM','9:30 AM','10:00 AM','10:30 AM',
  '11:00 AM','11:30 AM','12:00 PM','12:30 PM','1:00 PM','1:30 PM',
  '2:00 PM','2:30 PM','3:00 PM','3:30 PM','4:00 PM','4:30 PM',
  '5:00 PM','5:30 PM','6:00 PM','6:30 PM','7:00 PM','7:30 PM','8:00 PM',
];
const QUICK_PICKS = [
  {label:'Today',days:0},{label:'Tomorrow',days:1},{label:'+3 days',days:3},{label:'+1 week',days:7},
];

function MiniCalendar({ selectedDate, minDate, onSelect }) {
  const today = new Date();
  const init  = selectedDate ? new Date(selectedDate + 'T12:00:00') : today;
  const [vy, setVy] = useState(init.getFullYear());
  const [vm, setVm] = useState(init.getMonth());
  const MONTHS=['January','February','March','April','May','June','July','August','September','October','November','December'];
  const DAYS=['Su','Mo','Tu','We','Th','Fr','Sa'];
  function prev(){if(vm===0){setVm(11);setVy(y=>y-1);}else setVm(m=>m-1);}
  function next(){if(vm===11){setVm(0);setVy(y=>y+1);}else setVm(m=>m+1);}
  const firstDay=new Date(vy,vm,1).getDay(), dim=new Date(vy,vm+1,0).getDate();
  const minISO=minDate||toISO(today);
  const cells=[];
  for(let i=0;i<firstDay;i++)cells.push(null);
  for(let d=1;d<=dim;d++)cells.push(d);
  return (
    <div style={{background:'#0d1117',border:'1px solid #333',borderRadius:8,padding:10,userSelect:'none'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
        <button onClick={prev} style={{background:'none',border:'none',color:'#888',fontSize:16,cursor:'pointer',padding:'0 6px'}}>‹</button>
        <span style={{fontSize:12,fontWeight:700,color:'#e5e5e5'}}>{MONTHS[vm]} {vy}</span>
        <button onClick={next} style={{background:'none',border:'none',color:'#888',fontSize:16,cursor:'pointer',padding:'0 6px'}}>›</button>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2,marginBottom:4}}>
        {DAYS.map(d=><div key={d} style={{textAlign:'center',fontSize:9,color:'#555',fontWeight:700}}>{d}</div>)}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2}}>
        {cells.map((day,i)=>{
          if(!day)return<div key={`e${i}`}/>;
          const iso=`${vy}-${String(vm+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const isSel=iso===selectedDate,isToday=iso===toISO(today),isPast=minDate&&iso<minISO;
          return(
            <button key={iso} disabled={isPast} onClick={()=>!isPast&&onSelect(iso)} style={{
              background:isSel?'#facc15':isToday?'#facc1520':'transparent',
              color:isSel?'#000':isPast?'#333':isToday?'#facc15':'#ccc',
              border:isSel?'none':isToday?'1px solid #facc1540':'1px solid transparent',
              borderRadius:4,padding:'4px 2px',fontSize:11,
              cursor:isPast?'not-allowed':'pointer',fontWeight:isSel?700:400,
            }}>{day}</button>
          );
        })}
      </div>
    </div>
  );
}

function DatePicker({ label, value, onChange, minDate }) {
  const [open,setOpen]=useState(false);
  const today=new Date();
  return (
    <div className="field-group">
      {label&&<label className="field-label">{label}</label>}
      <button type="button" onClick={()=>setOpen(p=>!p)} style={{
        width:'100%',textAlign:'left',background:'#111',
        border:`1px solid ${value?'#facc15':'#333'}`,
        borderRadius:6,padding:'7px 12px',fontSize:13,
        color:value?'#facc15':'#555',cursor:'pointer',
      }}>
        {value?`📅 ${fmtDisplay(value)}`:'📅 Select date…'}
      </button>
      {open&&(
        <div style={{marginTop:6}}>
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8}}>
            {QUICK_PICKS.map(p=>(
              <button key={p.label} type="button"
                onClick={()=>{onChange(toISO(addDays(today,p.days)));setOpen(false);}}
                style={{fontSize:11,padding:'4px 10px',borderRadius:4,cursor:'pointer',border:'1px solid #333',background:'#111',color:'#888'}}>
                {p.label}
              </button>
            ))}
          </div>
          <MiniCalendar selectedDate={value} minDate={minDate} onSelect={d=>{onChange(d);setOpen(false);}}/>
          {value&&<button type="button" onClick={()=>onChange('')} style={{background:'none',border:'none',color:'#666',fontSize:11,cursor:'pointer',marginTop:4}}>✕ Clear</button>}
        </div>
      )}
    </div>
  );
}

function InlineScheduler({ value, onChange, label, color='#22c55e' }) {
  const [open,setOpen]=useState(false);
  const today=new Date();
  return (
    <div style={{background:`${color}08`,border:`1px solid ${color}33`,borderRadius:8,padding:'10px 12px',marginTop:8}}>
      <div style={{fontSize:11,fontWeight:700,color,marginBottom:8}}>📅 {label||'Schedule Follow-Up'}</div>
      <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8}}>
        {QUICK_PICKS.map(p=>{
          const iso=toISO(addDays(today,p.days));
          return(
            <button key={p.label} type="button"
              onClick={()=>{onChange({...value,date:iso});setOpen(false);}}
              style={{
                fontSize:11,padding:'4px 10px',borderRadius:4,cursor:'pointer',
                border:`1px solid ${value.date===iso?color:'#333'}`,
                background:value.date===iso?`${color}15`:'#111',
                color:value.date===iso?color:'#888',
              }}>{p.label}</button>
          );
        })}
        <button type="button" onClick={()=>setOpen(p=>!p)} style={{fontSize:11,padding:'4px 10px',borderRadius:4,cursor:'pointer',border:'1px solid #333',background:'#111',color:'#888'}}>📅 Pick date</button>
      </div>
      {open&&(
        <div style={{marginBottom:8}}>
          <MiniCalendar selectedDate={value.date} minDate={toISO(today)} onSelect={d=>{onChange({...value,date:d});setOpen(false);}}/>
        </div>
      )}
      {value.date&&(
        <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
          <span style={{fontSize:12,color,fontWeight:600}}>✓ {fmtDisplay(value.date)}</span>
          <select value={value.time||''} onChange={e=>onChange({...value,time:e.target.value||null})}
            style={{background:'#111',border:'1px solid #333',color:value.time?color:'#666',borderRadius:5,padding:'4px 8px',fontSize:11}}>
            <option value=''>Any time</option>
            {TIME_OPTIONS.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
          <button type="button" onClick={()=>onChange({date:null,time:null})}
            style={{background:'none',border:'none',color:'#666',fontSize:11,cursor:'pointer'}}>✕ clear</button>
        </div>
      )}
    </div>
  );
}

function Badge({color,children}){return<span style={{background:color+'22',color,border:`1px solid ${color}`,borderRadius:4,padding:'2px 8px',fontSize:11,fontWeight:700}}>{children}</span>;}
function FieldRow({label,value}){return(<div style={{display:'flex',alignItems:'flex-start',padding:'8px 0',borderBottom:'1px solid #111'}}><div style={{width:180,flexShrink:0,fontSize:11,color:'#666',textTransform:'uppercase',letterSpacing:'0.05em',paddingTop:2}}>{label}</div><div style={{flex:1,fontSize:13,color:'#e5e5e5'}}>{value??<span style={{color:'#444'}}>—</span>}</div></div>);}
function SectionTitle({children}){return<div style={{fontSize:11,fontWeight:700,color:'#666',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8,marginTop:20}}>{children}</div>;}
function OwnershipHeader({owner}){
  const cfg={'F. DESK':{color:'#3b82f6',bg:'#3b82f611',border:'#3b82f633'},'SALES OR F. DESK':{color:'#facc15',bg:'#facc1511',border:'#facc1533'},'SALES':{color:'#22c55e',bg:'#22c55e11',border:'#22c55e33'}}[owner]||{color:'#3b82f6',bg:'#3b82f611',border:'#3b82f633'};
  return<div style={{display:'inline-flex',alignItems:'center',gap:6,background:cfg.bg,border:`1px solid ${cfg.border}`,borderRadius:4,padding:'2px 8px',fontSize:9,fontWeight:700,color:cfg.color,letterSpacing:'0.06em',textTransform:'uppercase',marginBottom:12}}>{owner}</div>;
}
function EditButton({onClick}){return<button onClick={onClick} style={{background:'none',border:'1px solid #facc15',color:'#facc15',borderRadius:5,padding:'4px 12px',fontSize:11,fontWeight:700,cursor:'pointer'}}>✎ Edit</button>;}
function SaveCancelRow({onSave,onCancel,saving}){return(<div style={{display:'flex',gap:8,marginTop:12}}><button onClick={onSave} disabled={saving} style={{background:'#facc15',color:'#000',border:'none',borderRadius:5,padding:'6px 16px',fontSize:12,fontWeight:700,cursor:'pointer'}}>{saving?'Saving…':'Save Changes'}</button><button onClick={onCancel} style={{background:'none',border:'1px solid #333',color:'#888',borderRadius:5,padding:'6px 14px',fontSize:12,cursor:'pointer'}}>Cancel</button></div>);}
function SubTab({label,active,onClick,badge}){return(<button onClick={onClick} style={{background:active?'#facc15':'transparent',color:active?'#000':'#888',border:`1px solid ${active?'#facc15':'#333'}`,borderRadius:6,padding:'5px 14px',fontSize:12,fontWeight:active?700:400,cursor:'pointer',whiteSpace:'nowrap',display:'flex',alignItems:'center',gap:6}}>{label}{badge!==undefined&&<span style={{background:active?'#00000033':'#333',color:active?'#000':'#aaa',borderRadius:10,padding:'0 6px',fontSize:10,fontWeight:700}}>{badge}</span>}</button>);}

function scriptKeyForRecord(record){
  const sr=record.sessionDateRaw||record.sessionDate;
  if(record.trialOfferedAgain)return'pb101_op_reengage';
  if(record.stayedForPlay===true||record.stayedForPlay==='true')return'pb101_trial_asked_stayed';
  if(record.attendedLtp===true||record.attendedLtp==='true')return'pb101_trial_ask_notstayed';
  if(sr){
    const today=new Date();today.setHours(0,0,0,0);
    const session=new Date(sr);session.setHours(0,0,0,0);
    const diff=Math.round((session-today)/86400000);
    if(diff<0)return'pb101_followup_1';
    if(diff===0)return'pb101_op_morning_of';
    if(diff===1)return'pb101_presession_1day';
    if(diff<=4)return'pb101_op_followup_24hr';
  }
  return'pb101_op_invite_registration';
}

function PB101ScriptPanel({record,clubId,role,userName,clubName}){
  const{get}=useApi();
  const repName=role==='fd'?`Picklr ${clubName||''}`:(userName||'[Rep Name]');
  const[script,setScript]=useState(null);
  const[allScripts,setAllScripts]=useState([]);
  const[collapsed,setCollapsed]=useState(false);
  const[pickerOpen,setPickerOpen]=useState(false);
  const[copied,setCopied]=useState(false);
  useEffect(()=>{
    get('/scripts?module=pb101').then(d=>{
      const scripts=d?.scripts||[];setAllScripts(scripts);
      const key=scriptKeyForRecord(record);
      const match=scripts.find(s=>s.script_key===key&&s.scope==='club'&&s.location_id===clubId)||scripts.find(s=>s.script_key===key)||scripts[0];
      if(match)setScript(match);
    }).catch(()=>{});
  },[record?.id]);// eslint-disable-line
  function interpolate(body){if(!body)return body;return body.replace(/\[Name\]/g,record.name?.split(' ')[0]||'[Name]').replace(/\[Rep Name\]/g,repName).replace(/\[Club Name\]/g,clubName||'[Club Name]');}
  function handleCopy(){if(!script?.body)return;navigator.clipboard.writeText(interpolate(script.body)).then(()=>{setCopied(true);setTimeout(()=>setCopied(false),2000);});}
  if(!script)return null;
  return(
    <div style={{background:'rgba(234,179,8,0.07)',border:'1px solid rgba(234,179,8,0.25)',borderRadius:8,padding:'10px 14px',marginBottom:12}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:collapsed?0:8}}>
        <span style={{fontSize:11,fontWeight:700,color:'#facc15',textTransform:'uppercase',letterSpacing:'0.06em'}}>📋 {script.name}</span>
        <button style={{background:'none',border:'none',color:'#666',cursor:'pointer',fontSize:11}} onClick={()=>setCollapsed(v=>!v)}>{collapsed?'▾ Show':'▴ Hide'}</button>
      </div>
      {!collapsed&&(<>
        {script.why_context&&<div style={{fontSize:11,color:'#60a5fa',fontStyle:'italic',marginBottom:8,lineHeight:1.4}}>💡 {script.why_context}</div>}
        {script.timing&&<div style={{fontSize:10,color:'#666',marginBottom:6}}>{script.timing}</div>}
        <div style={{fontSize:12,color:'#e5e5e5',lineHeight:1.6,padding:'8px 10px',background:'#111',borderRadius:6,marginBottom:8,whiteSpace:'pre-wrap'}}>{interpolate(script.body)}</div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
          <button onClick={handleCopy} style={{background:'#facc15',color:'#000',border:'none',borderRadius:5,padding:'4px 10px',fontSize:11,fontWeight:700,cursor:'pointer'}}>{copied?'✓ Copied':'📋 Copy'}</button>
          <button onClick={()=>setPickerOpen(v=>!v)} style={{background:'none',border:'1px solid #333',color:'#888',borderRadius:5,padding:'4px 10px',fontSize:11,cursor:'pointer'}}>↕ Switch script</button>
        </div>
        {pickerOpen&&(
          <div style={{marginTop:8,border:'1px solid #222',borderRadius:6,overflow:'hidden'}}>
            <div style={{fontSize:10,fontWeight:700,color:'#666',textTransform:'uppercase',letterSpacing:'0.06em',padding:'6px 10px',background:'#1a1a1a',borderBottom:'1px solid #222'}}>PB101 Scripts — tap to switch</div>
            {allScripts.map((s,i)=>(
              <button key={s.script_id||i} onClick={()=>{setScript(s);setPickerOpen(false);}} style={{width:'100%',textAlign:'left',padding:'7px 10px',background:s.script_id===script.script_id?'rgba(234,179,8,0.1)':'#111',border:'none',borderBottom:i<allScripts.length-1?'1px solid #1a1a1a':'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div><div style={{fontSize:11,fontWeight:600,color:'#e5e5e5'}}>{s.name}</div>{s.timing&&<div style={{fontSize:10,color:'#666',marginTop:1}}>{s.timing}</div>}</div>
                {s.script_id===script.script_id&&<span style={{fontSize:9,fontWeight:700,color:'#facc15',background:'rgba(234,179,8,0.15)',padding:'1px 5px',borderRadius:3}}>CURRENT</span>}
              </button>
            ))}
          </div>
        )}
      </>)}
    </div>
  );
}

// ─── Tab: Profile ─────────────────────────────────────────────────────────────

function ProfileTab({record,onEdit}){
  return(
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
        <OwnershipHeader owner="F. DESK"/>
        <EditButton onClick={onEdit}/>
      </div>
      <SectionTitle>Contact Info</SectionTitle>
      <FieldRow label="Name" value={record.name}/>
      <FieldRow label="Email" value={record.email}/>
      <FieldRow label="Phone" value={record.phone}/>
      <FieldRow label="Lead Source" value={record.leadSource}/>
         <SectionTitle>Session</SectionTitle>
      <FieldRow label="PB101 Date" value={fmtDate(record.sessionDateRaw||record.sessionDate)}/>
      <FieldRow label="OP Invited" value={yesNo(record.openPlayInvited??record.opInvited)}/>
      {record.openPlayInvitedBy&&<FieldRow label="Invited By" value={record.openPlayInvitedBy}/>}
      <FieldRow label="OP Reserved" value={yesNo(record.openPlayReserved??record.opReserved)}/>
      {record.opInviteDate&&<FieldRow label="OP Invite Date" value={fmtDate(record.opInviteDate)}/>}
      <SectionTitle>Current Status</SectionTitle>
      <div style={{background:'#111',border:'1px solid #222',borderRadius:8,padding:'12px 16px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px 24px'}}>
        {[['Record Stage',record.status||'Active'],['Trial Ask',record.trialAskResult||'—'],['Attempts',record.attempt||0],['Next Action',record.nextAction||'—']].map(([l,v])=>(
          <div key={l}>
            <div style={{fontSize:10,color:'#666',textTransform:'uppercase',letterSpacing:'0.05em'}}>{l}</div>
            <div style={{fontSize:13,color:l==='Next Action'?'#facc15':'#e5e5e5',marginTop:2,fontWeight:l==='Record Stage'?600:400}}>{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProfileEditForm({record,onSave,onCancel}){
  const[form,setForm]=useState({
    phone:record.phone||'',email:record.email||'',
    sessionDate:record.sessionDateRaw?new Date(record.sessionDateRaw).toISOString().split('T')[0]:'',
    leadSource:record.leadSource||'',
    openPlayInvited:record.openPlayInvited===true||record.opInvited===true,
    openPlayInvitedBy:record.openPlayInvitedBy||'',
    openPlayReserved:record.openPlayReserved===true||record.opReserved===true,
    opInviteDate:record.opInviteDate?new Date(record.opInviteDate).toISOString().split('T')[0]:'',
  });
  const[saving,setSaving]=useState(false);
  function upd(f,v){setForm(x=>({...x,[f]:v}));}
  async function handleSave(){
    setSaving(true);
    await onSave({phone:form.phone||null,email:form.email||null,ltp_date:form.sessionDate||null,lead_source:form.leadSource||null,open_play_invited:form.openPlayInvited,open_play_invited_by:form.openPlayInvitedBy.trim()||null,open_play_reserved:form.openPlayReserved,op_invite_date:form.opInviteDate||null});
    setSaving(false);
  }
  return(
    <div>
      <div style={{fontSize:12,fontWeight:700,color:'#facc15',marginBottom:12,textTransform:'uppercase',letterSpacing:'0.06em'}}>✎ Editing Profile</div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem',marginBottom:'0.75rem'}}>
        <div className="field-group"><label className="field-label">Phone</label><input className="field-input" value={form.phone} onChange={e=>upd('phone',e.target.value)} placeholder="10-digit"/></div>
        <div className="field-group"><label className="field-label">Email</label><input className="field-input" value={form.email} onChange={e=>upd('email',e.target.value)}/></div>
        
        <div className="field-group"><label className="field-label">Lead Source</label>
          <select className="field-select" value={form.leadSource} onChange={e=>upd('leadSource',e.target.value)}>
            <option value="">Select...</option>{LEAD_SOURCES.map(s=><option key={s} value={s}>{s}</option>)}
          </select></div>
        <div style={{gridColumn:'span 2'}}>
          <DatePicker label="Session Date" value={form.sessionDate} onChange={v=>upd('sessionDate',v)}/>
        </div>
      </div>
      <div style={{display:'flex',gap:'1.5rem',marginBottom:'0.75rem',flexWrap:'wrap'}}>
        {[['openPlayInvited','OP Invited'],['openPlayReserved','OP Reserved']].map(([f,l])=>(
          <label key={f} style={{display:'flex',alignItems:'center',gap:'0.5rem',cursor:'pointer',fontSize:'0.85rem',color:'#e5e5e5'}}>
            <input type="checkbox" checked={!!form[f]} onChange={e=>upd(f,e.target.checked)}/>{l}
          </label>
        ))}
      </div>
      {form.openPlayInvited && (
        <div className="field-group" style={{marginBottom:'0.75rem'}}>
          <label className="field-label">Invited By</label>
          <input className="field-input" value={form.openPlayInvitedBy}
            placeholder="Name of whoever made the invite"
            onChange={e=>upd('openPlayInvitedBy',e.target.value)}/>
        </div>
      )}
      <SaveCancelRow onSave={handleSave} onCancel={onCancel} saving={saving}/>
    </div>
  );
}

// ─── Tab: PB101 Event + Trial Ask ─────────────────────────────────────────────

function EventTab({record,onEdit}){
  const isHotLead=record.trialAskResult==='Sales Follow-Up Needed';
  const isSold=['Sold Trial','Sold Membership','Sold Punch Card'].includes(record.trialAskResult);
  return(
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
        <OwnershipHeader owner="SALES OR F. DESK"/>
        <EditButton onClick={onEdit}/>
      </div>
      {isHotLead&&<div style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.4)',borderRadius:7,padding:'8px 14px',marginBottom:12,fontSize:13,color:'#ef4444',fontWeight:800}}>🔥 HOT LEAD — Sales follow-up needed within 4 hours</div>}
      {isSold&&<div style={{background:'rgba(34,197,94,0.1)',border:'1px solid rgba(34,197,94,0.4)',borderRadius:7,padding:'8px 14px',marginBottom:12,fontSize:13,color:'#22c55e',fontWeight:800}}>✅ {record.trialAskResult}{record.saleType?` — ${record.saleType}`:''}</div>}
      <SectionTitle>Attendance</SectionTitle>
      <FieldRow label="Attended PB101" value={yesNo(record.attendedLtp)}/>
      <FieldRow label="Stayed for Play" value={yesNo(record.stayedForPlay)}/>
      <SectionTitle>Day-of Trial Ask</SectionTitle>
      <FieldRow label="Trial Ask Result" value={record.trialAskResult}/>
      <FieldRow label="Offered By" value={record.trialOfferedBy}/>
      {record.saleType&&<FieldRow label="Sale Type" value={record.saleType}/>}
      {record.trialStartDate&&<FieldRow label="Trial Start Date" value={fmtDate(record.trialStartDate)}/>}
    </div>
  );
}

function EventEditForm({record,onSave,onCancel}){
  const[form,setForm]=useState({
    attendedLtp:record.attendedLtp===true||record.attendedLtp==='true',
    stayedForPlay:record.stayedForPlay===true||record.stayedForPlay==='true',
    trialAskResult:record.trialAskResult||'',
    trialOfferedBy:record.trialOfferedBy||'',
    saleType:record.saleType||'',
    trialStartDate:record.trialStartDate?new Date(record.trialStartDate).toISOString().split('T')[0]:'',
    followUpDate:null,followUpTime:null,
  });
  const[saving,setSaving]=useState(false);
  function upd(f,v){setForm(x=>({...x,[f]:v}));}
  const isSold=['Sold Trial','Sold Membership','Sold Punch Card'].includes(form.trialAskResult);
  const isSalesHandoff=form.trialAskResult==='Sales Follow-Up Needed';
  const isDeclined=form.trialAskResult&&(form.trialAskResult.startsWith('Declined')||['Failed to Ask','No Show'].includes(form.trialAskResult));
  async function handleSave(){
    setSaving(true);
    await onSave({attended_ltp:form.attendedLtp,stayed_for_play:form.stayedForPlay,trial_ask_result:form.trialAskResult||null,trial_offered_by:form.trialOfferedBy||null,sale_type:form.saleType||null,trial_start_date:form.trialStartDate||null,follow_up_date:form.followUpDate||null,follow_up_time:form.followUpTime||null});
    setSaving(false);
  }
  return(
    <div>
      <div style={{fontSize:12,fontWeight:700,color:'#facc15',marginBottom:12,textTransform:'uppercase',letterSpacing:'0.06em'}}>✎ Editing PB101 Event</div>
      <div style={{fontSize:11,fontWeight:700,color:'#666',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8}}>Attendance</div>
      <div style={{display:'flex',gap:'1.5rem',marginBottom:'0.75rem',flexWrap:'wrap'}}>
        {[['attendedLtp','Attended PB101'],['stayedForPlay','Stayed for Play']].map(([f,l])=>(
          <label key={f} style={{display:'flex',alignItems:'center',gap:'0.5rem',cursor:'pointer',fontSize:'0.85rem',color:'#e5e5e5'}}>
            <input type="checkbox" checked={!!form[f]} onChange={e=>upd(f,e.target.checked)}/>{l}
          </label>
        ))}
      </div>
      <div style={{fontSize:11,fontWeight:700,color:'#666',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8,marginTop:16}}>Day-of Trial Ask</div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:'0.75rem'}}>
        {TRIAL_ASK_RESULTS.map(r=>{
          const sold=['Sold Trial','Sold Membership','Sold Punch Card'].includes(r);
          const handoff=r==='Sales Follow-Up Needed';
          const color=sold?'#22c55e':handoff?'#ef4444':'#555';
          const selected=form.trialAskResult===r;
          return(
            <button key={r} type="button" onClick={()=>upd('trialAskResult',selected?'':r)} style={{
              textAlign:'left',padding:'7px 10px',borderRadius:6,cursor:'pointer',
              border:`1px solid ${selected?color:'#2a2a2a'}`,
              background:selected?color+'18':'#0d0d0d',
              color:selected?color:'#666',fontSize:11,fontWeight:selected?700:400,
            }}>{sold?'✅ ':handoff?'🔥 ':''}{r}</button>
          );
        })}
      </div>
      {isSalesHandoff&&<div style={{background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.35)',borderRadius:7,padding:'8px 12px',marginBottom:10,fontSize:12,color:'#ef4444',fontWeight:700}}>🔥 HOT LEAD — Schedule when Sales should follow up:</div>}
      {isSold&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem',marginBottom:'0.75rem'}}>
          <div className="field-group"><label className="field-label">Sale Type</label>
            <select className="field-select" value={form.saleType} onChange={e=>upd('saleType',e.target.value)}>
              <option value="">Select...</option>{SALE_TYPES.map(s=><option key={s} value={s}>{s}</option>)}
            </select></div>
          <DatePicker label="Trial Start Date" value={form.trialStartDate} onChange={v=>upd('trialStartDate',v)}/>
        </div>
      )}
      {(isSalesHandoff||isDeclined)&&(
        <InlineScheduler
          value={{date:form.followUpDate,time:form.followUpTime}}
          onChange={v=>{upd('followUpDate',v.date);upd('followUpTime',v.time);}}
          label={isSalesHandoff?'Schedule Sales Follow-Up (required within 4 hours)':'Schedule Sales Follow-Up (optional)'}
          color={isSalesHandoff?'#ef4444':'#22c55e'}
        />
      )}
      <div className="field-group" style={{marginTop:'0.75rem'}}>
        <label className="field-label">Trial Offered By</label>
        <input className="field-input" value={form.trialOfferedBy} onChange={e=>upd('trialOfferedBy',e.target.value)} placeholder="Staff name"/>
      </div>
      <SaveCancelRow onSave={handleSave} onCancel={onCancel} saving={saving}/>
    </div>
  );
}

// ─── Tab: Follow-Up (Sales) ───────────────────────────────────────────────────

function FollowUpTab({record,onLogContact}){
  return(
    <div>
      <OwnershipHeader owner="SALES"/>
      {record.recurringConverted?(
        <div style={{background:'rgba(34,197,94,0.1)',border:'1px solid rgba(34,197,94,0.4)',borderRadius:7,padding:'10px 14px',marginBottom:12,fontSize:13,color:'#22c55e',fontWeight:800}}>
          ✅ MEMBERSHIP CONVERTED{record.saleType?` — ${record.saleType}`:''}
        </div>
      ):(
        <div style={{marginBottom:12}}>
          <button onClick={onLogContact} style={{background:'#facc15',color:'#000',border:'none',borderRadius:6,padding:'10px 20px',fontSize:13,fontWeight:700,cursor:'pointer',width:'100%'}}>
            📞 Log Contact / Outcome
          </button>
          <div style={{fontSize:11,color:'#555',marginTop:6,textAlign:'center'}}>Log every contact attempt, outcome, and follow-up here</div>
        </div>
      )}
      <SectionTitle>Conversion Status</SectionTitle>
      <FieldRow label="Converted" value={yesNo(record.recurringConverted)}/>
      <FieldRow label="Sale Type" value={record.saleType}/>
      {record.trialStartDate&&<FieldRow label="Trial Start Date" value={fmtDate(record.trialStartDate)}/>}
      <FieldRow label="Follow-Up Sent" value={yesNo(record.followupSent)}/>
      <FieldRow label="Trial Offered Again" value={yesNo(record.trialOfferedAgain)}/>
      <FieldRow label="Next Action" value={record.nextAction}/>
      {record.notes&&(<><SectionTitle>Notes</SectionTitle><div style={{background:'#111',border:'1px solid #222',borderRadius:6,padding:'10px 14px',fontSize:13,color:'#ccc',lineHeight:1.5}}>{record.notes}</div></>)}
    </div>
  );
}

// ─── Tab: Contact History ─────────────────────────────────────────────────────
// Sprint 21 continuation — this used to be a bespoke component that fetched
// /member/timeline/{memberId} and then filtered the results down to
// module==='pb101' only, hiding any T2M/Lead Engine history for the same
// real person. Replaced with the shared MemberTimeline component (same one
// LeadRecordPanel already uses for T2M) — same correct, member-scoped data,
// shown in full rather than narrowed to just this one module. See its own
// module filter pills if a PB101-only view is still wanted in the moment.

// ─── Main ─────────────────────────────────────────────────────────────────────

const SUBTABS=['Profile','PB101 Event','Follow-Up','Contact History'];

export default function PB101RecordDetail({recordId,onBack,onLogContact}){
  const{get,put}=useApi();
  const{currentClub}=useClub();
  const{role,user}=useAuth();
  const[record,setRecord]=useState(null);
  const[loading,setLoading]=useState(true);
  const[subTab,setSubTab]=useState('Profile');
  const[error,setError]=useState(null);
  const[editingTab,setEditingTab]=useState(null);

  async function handleTabSave(fields){await put(`/pb101/record/${recordId}`,fields);setEditingTab(null);load();}

  const load=useCallback(async()=>{
    if(!recordId)return;
    setLoading(true);setError(null);
    try{const data=await get(`/pb101/record/${recordId}`);setRecord(data);}
    catch(e){setError('Failed to load record. '+(e.message||''));}
    finally{setLoading(false);}
  },[recordId]);// eslint-disable-line

  useEffect(()=>{load();},[load]);

  if(loading)return<div style={{padding:32,textAlign:'center',color:'#666'}}>Loading record…</div>;
  if(error)return(<div style={{padding:32,textAlign:'center',color:'#ef4444'}}>{error}<br/><button onClick={load} style={{marginTop:12,background:'#facc15',color:'#000',border:'none',borderRadius:6,padding:'8px 20px',cursor:'pointer',fontWeight:700}}>Try Again</button></div>);
  if(!record)return null;

  const isHotLead=record.trialAskResult==='Sales Follow-Up Needed';
  const isSold=['Sold Trial','Sold Membership','Sold Punch Card'].includes(record.trialAskResult);
  const statusColors={converted:'#22c55e',lost:'#ef4444',declined:'#6b7280',active:'#3b82f6'};
  const statusCol=statusColors[record.status?.toLowerCase()]||'#eab308';

  return(
    <div style={{fontFamily:'DM Sans, sans-serif',maxWidth:860,margin:'0 auto'}}>
      <div style={{marginBottom:16}}>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:10,flexWrap:'wrap'}}>
          <button onClick={onBack} style={{background:'transparent',color:'#888',border:'1px solid #333',borderRadius:6,padding:'5px 12px',fontSize:12,cursor:'pointer'}}>← Back</button>
          <span style={{fontWeight:800,fontSize:18,color:'#e5e5e5'}}>{record.name}</span>
          <span style={{background:statusCol+'22',color:statusCol,border:`1px solid ${statusCol}`,borderRadius:4,padding:'2px 8px',fontSize:11,fontWeight:700}}>{record.status||'Active'}</span>
          {isHotLead&&<span style={{background:'rgba(239,68,68,0.15)',color:'#ef4444',border:'1px solid rgba(239,68,68,0.5)',borderRadius:4,padding:'2px 10px',fontSize:11,fontWeight:800}}>🔥 HOT LEAD</span>}
          {isSold&&!record.recurringConverted&&<span style={{background:'rgba(34,197,94,0.15)',color:'#22c55e',border:'1px solid rgba(34,197,94,0.5)',borderRadius:4,padding:'2px 10px',fontSize:11,fontWeight:800}}>{record.trialAskResult}</span>}
          {record.recurringConverted&&<span style={{background:'rgba(34,197,94,0.2)',color:'#22c55e',border:'1px solid #22c55e',borderRadius:4,padding:'2px 10px',fontSize:12,fontWeight:800}}>✅ MEMBERSHIP CONVERTED{record.saleType?` — ${record.saleType}`:''}</span>}
        </div>
        <div style={{fontSize:12,color:'#666',display:'flex',gap:16,flexWrap:'wrap'}}>
          {record.sessionDate&&<span>📅 PB101: {record.sessionDate}</span>}
          {record.nextAction&&<span style={{color:'#facc15'}}>→ {record.nextAction}</span>}
        </div>
      </div>

      <div style={{display:'flex',gap:16,alignItems:'flex-start'}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:16}}>
            {SUBTABS.map(t=>(
              <SubTab key={t} label={t} active={subTab===t}
                onClick={()=>{setSubTab(t);setEditingTab(null);}}
              />
            ))}
          </div>
          <PB101ScriptPanel record={record} clubId={record.clubId} role={role} userName={user?.name} clubName={currentClub?.name}/>
          <div style={{background:'#0d0d0d',border:'1px solid #1a1a1a',borderRadius:8,padding:'14px 16px'}}>
            {subTab==='Profile'&&editingTab!=='Profile'&&<ProfileTab record={record} onEdit={()=>setEditingTab('Profile')}/>}
            {subTab==='Profile'&&editingTab==='Profile'&&<ProfileEditForm record={record} onSave={handleTabSave} onCancel={()=>setEditingTab(null)}/>}
            {subTab==='PB101 Event'&&editingTab!=='PB101 Event'&&<EventTab record={record} onEdit={()=>setEditingTab('PB101 Event')}/>}
            {subTab==='PB101 Event'&&editingTab==='PB101 Event'&&<EventEditForm record={record} onSave={handleTabSave} onCancel={()=>setEditingTab(null)}/>}
            {subTab==='Follow-Up'&&<FollowUpTab record={record} onLogContact={()=>onLogContact&&onLogContact(record)}/>}
            {subTab==='Contact History'&&<MemberTimeline memberId={record.memberId} memberName={record.name} compact/>}
          </div>
        </div>
        <div style={{width:200,flexShrink:0}}>
          <div style={{background:'#0a0a0a',border:'1px solid #1a1a1a',borderRadius:8,padding:'14px 16px'}}>
            <div style={{fontSize:11,color:'#666',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:10}}>Quick Actions</div>
            {isHotLead&&<div style={{background:'rgba(239,68,68,0.12)',border:'1px solid rgba(239,68,68,0.4)',borderRadius:6,padding:'5px 10px',marginBottom:8,fontSize:11,color:'#ef4444',fontWeight:800,textAlign:'center'}}>🔥 HOT LEAD</div>}
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              <button onClick={()=>onLogContact&&onLogContact(record)} style={{background:'#facc15',color:'#000',border:'none',borderRadius:6,padding:'8px 14px',fontSize:12,fontWeight:700,cursor:'pointer',textAlign:'left'}}>📞 Log Contact</button>
              {(record.status==='lost'||record.status==='declined')&&(
                <button style={{background:'transparent',color:'#22c55e',border:'1px solid #22c55e',borderRadius:6,padding:'7px 14px',fontSize:12,cursor:'pointer',textAlign:'left'}}>↩ They Responded — Re-open</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
